import type {Ref} from 'vue';
import {computed, ref, unref, watch} from 'vue';
import cloneDeep from 'lodash-es/cloneDeep';
import debounce from 'lodash-es/debounce';
import type {MaybeRef} from '@malt/types-utils';
import type {SharedBreakpointsOptions} from './types';
import {BreakPointsTypes, BreakPointsValues} from './types';

interface useBreakPointOperationsOptions {
    container?: MaybeRef<HTMLElement | undefined>;
    containerWidth: Ref<number>;
    containerHeight: Ref<number>;
    options?: SharedBreakpointsOptions;
    layoutPaddingLeft?: Ref<number | null>;
    type: 'window' | 'container';
}

const defaultMediaValues = {
    Tiny: false,
    Xxs: false,
    Xs: false,
    Sm: false,
    Md: false,
    Lg: false,
    Xl: false,
};

/** Only to reuse logic between useMediaQuery */
export function useBreakpointOperations({
    container,
    containerHeight,
    containerWidth,
    options,
    layoutPaddingLeft,
    type,
}: useBreakPointOperationsOptions) {
    const unwrappedContainer = computed(() => unref(container));

    const currentBreakpoint = computed<BreakPointsTypes>(() => {
        const activeBreakpoint = (Object.entries(BreakPointsValues) as Array<[BreakPointsTypes, BreakPointsValues]>).find(
            ([_, value]) => containerWidth.value <= (value as number),
        );

        if (activeBreakpoint) {
            return BreakPointsTypes[activeBreakpoint[0]];
        } else {
            return BreakPointsTypes.Xl;
        }
    });

    function handleResize() {
        if (unwrappedContainer.value) {
            if (type === 'window') {
                containerWidth.value = unwrappedContainer.value.clientWidth || window.innerWidth;
                containerHeight.value = unwrappedContainer.value.clientHeight || window.innerHeight;
            } else {
                const {width, height} = unwrappedContainer.value.getBoundingClientRect();
                containerWidth.value = width;
                containerHeight.value = height;
            }
            if (options?.onResize) {
                options.onResize({width: containerWidth.value, height: containerHeight.value});
            }
        }
    }

    const matchMedia = ref<{max: Record<BreakPointsTypes, boolean>; min: Record<BreakPointsTypes, boolean>}>({
        max: cloneDeep(defaultMediaValues),
        min: cloneDeep(defaultMediaValues),
    });

    const matchNavbarMedia = ref<{max: Record<BreakPointsTypes, boolean>; min: Record<BreakPointsTypes, boolean>}>({
        max: cloneDeep(defaultMediaValues),
        min: cloneDeep(defaultMediaValues),
    });

    function matchMediaHandler() {
        matchMedia.value = {
            max: Object.fromEntries(
                Object.entries(BreakPointsValues).map(([key, value]) => {
                    return [key, containerWidth.value <= (value as number)];
                }),
            ) as Record<BreakPointsTypes, boolean>,
            min: Object.fromEntries(
                Object.entries(BreakPointsValues).map(([key, value]) => {
                    return [key, containerWidth.value >= (value as number)];
                }),
            ) as Record<BreakPointsTypes, boolean>,
        };

        matchNavbarMedia.value = {
            max: Object.fromEntries(
                Object.entries(BreakPointsValues).map(([key, value]) => {
                    return [key, containerWidth.value - (layoutPaddingLeft?.value ?? 0) <= (value as number)];
                }),
            ) as Record<BreakPointsTypes, boolean>,
            min: Object.fromEntries(
                Object.entries(BreakPointsValues).map(([key, value]) => {
                    return [key, containerWidth.value - (layoutPaddingLeft?.value ?? 0) >= (value as number)];
                }),
            ) as Record<BreakPointsTypes, boolean>,
        };
    }

    if (import.meta.client) {
        const handleMatch = debounce(matchMediaHandler, 0);
        watch(containerWidth, handleMatch);
    }

    return {
        currentBreakpoint,
        handleResize,
        matchMedia,
        matchNavbarMedia,
        handleMatch: matchMediaHandler,
    };
}
